import { ConfigSlideId, PhoneConfigField } from '@/eventConfig/types.ts';
import { ClipboardEventHandler, FC, MouseEventHandler, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import flags from 'react-phone-number-input/flags';
import PhoneInput, { Value } from 'react-phone-number-input';
import de from 'react-phone-number-input/locale/de';
import es from 'react-phone-number-input/locale/es';
import en from 'react-phone-number-input/locale/en';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import 'react-phone-number-input/style.css';
import { FieldType, FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { Country } from 'react-phone-number-input/min';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { PhoneUtil } from '@/util/phoneUtils';
import { useFieldValidation } from '../_pipeline/useValidateField';
import { useRevalidateField } from '@/util/useRevalidateField';

interface TextInputProps {
  field: PhoneConfigField;
  onUIEvent?: (e: any, target: any) => void;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const PhoneNumberInput: FC<TextInputProps> = ({ field, slideId, onChange, getFieldStatus, getFieldValue }) => {
  const { eventConfig } = useEventConfig();
  const { handleUiEvent } = useUiEvent();
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { revalidateFields } = useRevalidateField(validateField);

  const country = useRef<Country>(eventConfig?.meta.country ?? ('DE' as Country));

  const fieldStatus = getFieldStatus(field.name);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const htmlId = 'id_field_' + field.name;
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');
  const placeholder = translate(field.placeholder);
  const prevStateRef = useRef(fieldStatus);

  useEffect(() => {
    const prevRawValue = prevStateRef.current?.value.raw;
    const currentRawValue = fieldStatus?.value.raw;

    if (field.revalidate && prevRawValue != currentRawValue) {
      revalidateFields(field.revalidate);
      prevStateRef.current = fieldStatus;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldStatus]);

  const getLocalizedLables = () => {
    switch (eventConfig?.meta.defaultLocale) {
      case 'de':
        return de;
      case 'es':
        return es;
      default:
        return en;
    }
  };

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = () => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const handleChange = (value: Value) => {
    const parsedPhoneNumber = parsePhoneNumber(value, { regionCode: country.current });
    const phoneNumber = parsedPhoneNumber.number?.e164;
    const fieldValue = { raw: phoneNumber as FieldType, normalized: PhoneUtil.formatPhoneNumber(phoneNumber ?? '') };
    const { hint, isValid } = validateField(field.validator, fieldValue);

    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
  };

  const handlePaste: ClipboardEventHandler = (e) => {
    e.preventDefault();
    const parsedPhoneNumber = parsePhoneNumber(e.clipboardData.getData('Text'), { regionCode: country.current });
    const phoneNumber = parsedPhoneNumber.number?.e164;

    if (phoneNumber) {
      const fieldValue = { raw: phoneNumber as FieldType, normalized: PhoneUtil.formatPhoneNumber(phoneNumber ?? '') };
      const { hint, isValid } = validateField(field.validator, fieldValue);

      onChange(field.name, { value: fieldValue, hint, isValid, slideId });
    }
  };

  const handleCountryChange = (changedCountry: Country) => {
    country.current = changedCountry ?? eventConfig?.meta.country ?? 'DE';
  };

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field.hint || '');

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>
        <PhoneInput
          labels={getLocalizedLables()}
          flags={flags}
          id={htmlId}
          className={field.name + ' phone-input'}
          defaultCountry={eventConfig?.meta.country}
          limitMaxLength={true}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          countries={field.countries}
          onCountryChange={handleCountryChange}
          initialValueFormat={field.countries ? 'national' : undefined}
          international={field.countries?.includes('US') ? false : true}
        />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
